import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useCategories } from '../../shared/hooks/useCategories';
import apiClient from '../../shared/services/apiService';
import API_CONFIG from '../../shared/config/api';

const AdminCategories = () => {
  const { categories: initialCategories, loading, error } = useCategories();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    slug: '',
    icon: '',
    description: '', 
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    active: true 
  });
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCategories(initialCategories);
    setFilteredCategories(initialCategories);
  }, [initialCategories]);

  // Search filter
  useEffect(() => {
    if (searchTerm) {
      const filtered = categories.filter(cat =>
        cat.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(categories);
    }
    setCurrentPage(1);
  }, [searchTerm, categories]);

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredCategories.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredCategories.length / recordsPerPage);

  // Handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCategories(currentRecords.map(cat => cat.id));
    } else {
      setSelectedCategories([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedCategories.includes(id)) {
      setSelectedCategories(selectedCategories.filter(catId => catId !== id));
    } else {
      setSelectedCategories([...selectedCategories, id]);
    }
  };

  const handleAddCategory = async () => {
    try {
      const newCategory = {
        id: Date.now(),
        name: formData.name,
        slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-'),
        icon: formData.icon,
        description: formData.description,
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        metaKeywords: formData.metaKeywords,
        active: formData.active,
        subcategories: 0
      };
      setCategories([...categories, newCategory]);
      setShowAddModal(false);
      setFormData({ name: '', slug: '', icon: '', description: '', metaTitle: '', metaDescription: '', metaKeywords: '', active: true });
    } catch (err) {
      console.error('Error adding category:', err);
    }
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug || '',
      icon: category.icon || '',
      description: category.description || '',
      metaTitle: category.metaTitle || '',
      metaDescription: category.metaDescription || '',
      metaKeywords: category.metaKeywords || '',
      active: category.active !== false
    });
    setShowEditModal(true);
  };

  const handleUpdateCategory = async () => {
    try {
      const updatedCategories = categories.map(cat =>
        cat.id === currentCategory.id
          ? { 
              ...cat, 
              name: formData.name, 
              slug: formData.slug,
              icon: formData.icon,
              description: formData.description, 
              metaTitle: formData.metaTitle,
              metaDescription: formData.metaDescription,
              metaKeywords: formData.metaKeywords,
              active: formData.active 
            }
          : cat
      );
      setCategories(updatedCategories);
      setShowEditModal(false);
      setCurrentCategory(null);
      setFormData({ name: '', slug: '', icon: '', description: '', metaTitle: '', metaDescription: '', metaKeywords: '', active: true });
    } catch (err) {
      console.error('Error updating category:', err);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        setCategories(categories.filter(cat => cat.id !== id));
        setSelectedCategories(selectedCategories.filter(catId => catId !== id));
      } catch (err) {
        console.error('Error deleting category:', err);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedCategories.length} catégorie(s) ?`)) {
      try {
        setCategories(categories.filter(cat => !selectedCategories.includes(cat.id)));
        setSelectedCategories([]);
      } catch (err) {
        console.error('Error deleting categories:', err);
      }
    }
  };

  const handleActivateSelected = () => {
    const updatedCategories = categories.map(cat =>
      selectedCategories.includes(cat.id) ? { ...cat, active: true } : cat
    );
    setCategories(updatedCategories);
    setSelectedCategories([]);
  };

  const handleDeactivateSelected = () => {
    const updatedCategories = categories.map(cat =>
      selectedCategories.includes(cat.id) ? { ...cat, active: false } : cat
    );
    setCategories(updatedCategories);
    setSelectedCategories([]);
  };

  return (
    <>
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1" style={{ fontSize: '28px', fontWeight: '700' }}>Catégories</h2>
                <p className="text-muted mb-0">
                  Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredCategories.length)} of {filteredCategories.length} entries
                </p>
              </div>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item active">Catégories</li>
                  <li className="breadcrumb-item active">List</li>
                </ol>
              </nav>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '600'
                }}
              >
                <i className="fas fa-plus me-2"></i>Add category
              </button>
              <button
                className="btn btn-secondary"
                style={{ borderRadius: '8px', padding: '10px 20px' }}
              >
                <i className="fas fa-sort me-2"></i>Reorder categories
              </button>
              <button
                className="btn btn-info"
                style={{ borderRadius: '8px', padding: '10px 20px' }}
              >
                <i className="fas fa-sync me-2"></i>Rebuild Categories Nodes
              </button>
              {selectedCategories.length > 0 && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={handleActivateSelected}
                    style={{ borderRadius: '8px', padding: '10px 20px' }}
                  >
                    <i className="fas fa-check me-2"></i>Activate
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={handleDeactivateSelected}
                    style={{ borderRadius: '8px', padding: '10px 20px' }}
                  >
                    <i className="fas fa-times me-2"></i>Disable
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteSelected}
                    style={{ borderRadius: '8px', padding: '10px 20px' }}
                  >
                    <i className="fas fa-trash me-2"></i>Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="row">
          <div className="col-12">
            <div className="card" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <div className="card-body">
                {/* Search and Records per page */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <select
                        className="form-select"
                        value={recordsPerPage}
                        onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                        style={{ width: 'auto', borderRadius: '8px', marginRight: '10px' }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-muted">records per page</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search categories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead style={{ background: '#f8f9fa' }}>
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={selectedCategories.length === currentRecords.length && currentRecords.length > 0}
                          />
                        </th>
                        <th></th>
                        <th>
                          <div className="d-flex align-items-center">
                            Name
                            <i className="fas fa-sort ms-2" style={{ fontSize: '12px', color: '#6b7280' }}></i>
                          </div>
                        </th>
                        <th>
                          <div className="d-flex align-items-center">
                            Subcategories
                            <i className="fas fa-sort ms-2" style={{ fontSize: '12px', color: '#6b7280' }}></i>
                          </div>
                        </th>
                        <th>Active</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : currentRecords.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <p className="text-muted mb-0">Aucune catégorie trouvée</p>
                          </td>
                        </tr>
                      ) : (
                        currentRecords.map((category) => (
                          <tr key={category.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedCategories.includes(category.id)}
                                onChange={() => handleSelectOne(category.id)}
                              />
                            </td>
                            <td>
                              <button className="btn btn-sm btn-link p-0">
                                <i className="fas fa-plus-circle" style={{ color: '#22c55e' }}></i>
                              </button>
                            </td>
                            <td>
                              <strong>{category.name}</strong>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">
                                {category.subcategories || 0} subcategory
                              </span>
                            </td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={category.active !== false}
                                  onChange={() => {
                                    const updatedCategories = categories.map(cat =>
                                      cat.id === category.id ? { ...cat, active: !cat.active } : cat
                                    );
                                    setCategories(updatedCategories);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-sm"
                                  onClick={() => handleEditCategory(category)}
                                  style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '6px 12px'
                                  }}
                                >
                                  <i className="fas fa-edit"></i> Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteCategory(category.id)}
                                  style={{ borderRadius: '6px', padding: '6px 12px' }}
                                >
                                  <i className="fas fa-trash"></i> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="text-muted">
                      Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredCategories.length)} of {filteredCategories.length} entries
                    </div>
                    <nav>
                      <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                            Previous
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, index) => (
                          <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(index + 1)}>
                              {index + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Category Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Category Name *</label>
            <input
              type="text"
              className="form-control"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter category name"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter description"
            ></textarea>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            />
            <label className="form-check-label">Active</label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddCategory}
            disabled={!formData.name}
            style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', border: 'none' }}
          >
            Add Category
          </button>
        </Modal.Footer>
      </Modal>

      {/* Edit Category Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Category</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Name */}
          <div className="mb-3">
            <label className="form-label fw-bold">Name *</label>
            <input
              type="text"
              className="form-control"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter category name"
            />
          </div>

          {/* Slug */}
          <div className="mb-3">
            <label className="form-label fw-bold">Slug</label>
            <input
              type="text"
              className="form-control"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="category-slug"
            />
            <small className="text-muted">Leave empty to auto-generate from name</small>
          </div>

          {/* Icon */}
          <div className="mb-3">
            <label className="form-label fw-bold">Icon</label>
            <input
              type="text"
              className="form-control"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="fa fa-briefcase"
            />
            <small className="text-muted">Font Awesome icon class (e.g., fa fa-briefcase)</small>
          </div>

          {/* Description */}
          <div className="mb-3">
            <label className="form-label fw-bold">Description</label>
            <textarea
              className="form-control"
              rows="4"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter category description"
            ></textarea>
          </div>

          {/* SEO Meta Tags Section */}
          <div className="border-top pt-3 mt-4">
            <h6 className="mb-3 fw-bold">SEO Meta Tags</h6>
            
            {/* Meta Title */}
            <div className="mb-3">
              <label className="form-label">Meta Title</label>
              <input
                type="text"
                className="form-control"
                value={formData.metaTitle}
                onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                placeholder="SEO title for search engines"
              />
              <small className="text-muted">Recommended: 50-60 characters</small>
            </div>

            {/* Meta Description */}
            <div className="mb-3">
              <label className="form-label">Meta Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.metaDescription}
                onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                placeholder="SEO description for search engines"
              ></textarea>
              <small className="text-muted">Recommended: 150-160 characters</small>
            </div>

            {/* Meta Keywords */}
            <div className="mb-3">
              <label className="form-label">Meta Keywords</label>
              <input
                type="text"
                className="form-control"
                value={formData.metaKeywords}
                onChange={(e) => setFormData({ ...formData, metaKeywords: e.target.value })}
                placeholder="keyword1, keyword2, keyword3"
              />
              <small className="text-muted">Separate keywords with commas</small>
            </div>
          </div>

          {/* Active Status */}
          <div className="form-check mt-4">
            <input
              className="form-check-input"
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              id="editActiveCheck"
            />
            <label className="form-check-label fw-bold" htmlFor="editActiveCheck">
              Active
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpdateCategory}
            disabled={!formData.name}
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
          >
            <i className="fas fa-save me-2"></i>Update Category
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminCategories;
